import { defineStore, acceptHMRUpdate } from 'pinia';
import { reactive } from 'vue';
import i18n from '@/js/plugins/i18n';
import useUiPlanningStore from '@/stores/ui/planning';
import useMultiprojectsProcessStore from '@/stores/multiprojects/process';
import constants from '@/js/constants';
import PlanningProcessStep from '@/models/PlanningProcessStep';
import usePlanningStore from './planning';
import useUndoRedoStore from '@/stores/undoredo';
import useSubprojectsStore from '@/stores/subprojects';
import usePlanningLanesStore from './lanes';
import usePlanningElementsStore from './elements';

const undoState = { config: {}, steps: [] };

function insertNewStep(planning, newstep) {
  const step = reactive(new PlanningProcessStep(planning, newstep));
  planning.process.steps.push(step);
  return step;
}

function set(planning, { config: newconfig, steps: newsteps }) {
  if (newconfig) Object.assign(planning.process.config, newconfig);
  if (newsteps) {
    planning.process.steps.splice(0, planning.process.steps.length);
    while (newsteps.length) {
      const newstep = newsteps.shift();
      insertNewStep(planning, newstep);
    }
  }
}

const store = defineStore('planning-process', {
  getters: {
    subprojectStepId2ProjectStepId() {
      // automatically map subprojects steps ids to project steps ids by using the label
      // [subprojectId.subprojectStepId => projectStepId or label]
      const { planning } = usePlanningStore();
      const stepsIdsMapping = {};
      const stepLabel2Id = planning.process.steps.reduce((acc, step) => { acc[step.label] = step.id; return acc; }, {});
      const visibleLanes = usePlanningLanesStore().getVisibleLanes();
      visibleLanes.forEach((lane) => {
        if (lane.project_id) {
          const subproject = useSubprojectsStore().getSubprojectByLaneId(lane.id);
          if (subproject) {
            subproject.process.steps.forEach((subprojectStep) => {
              if (! subprojectStep.id) return; // skip backlog
              stepsIdsMapping[`${subproject.id}.${subprojectStep.id}`] = stepLabel2Id[subprojectStep.label] || subprojectStep.label;
            });
          }
        }
      });
      return stepsIdsMapping;
    },

    convertSubprojectStepId() {
      return (subprojectId, stepId) => {
        if (useUiPlanningStore().dashboard) return useMultiprojectsProcessStore().projectStepId2DashboardStepId[`${subprojectId}.${stepId}`]; // process dashboard conversion table
        return this.subprojectStepId2ProjectStepId[`${subprojectId}.${stepId}`];
      };
    },

    subprojectsProcessSteps() {
      const { planning } = usePlanningStore();
      const visibleLanes = usePlanningLanesStore().getVisibleLanes();
      const hiddenSubSteps = new Set(planning.process.config.getHiddenSubsteps()); // will get xs width

      const processStepsIds = new Set(planning.process.steps.map(item => item.id));
      return visibleLanes.reduce((acc, lane) => {
        if (lane.project_id) {
          const subproject = useSubprojectsStore().getSubprojectByLaneId(lane.id);
          if (subproject) {
            subproject.process.steps.forEach((subprojectStep) => {
              if (! subprojectStep.id) return; // skip backlog
              const newId = this.convertSubprojectStepId(subproject.id, subprojectStep.id); // newId should always exist by construction
              if (! newId || processStepsIds.has(newId)) return; // skip if col label already exists (merge)
              processStepsIds.add(newId);
              const subprojectStepCopy = angular.copy(subprojectStep);
              subprojectStepCopy.o_id = subprojectStep.id;
              subprojectStepCopy.id = newId;
              subprojectStepCopy.width = hiddenSubSteps.has(newId) ? constants.kanbanXsColWidth : constants.kanbanDefaultColWidth;
              acc.push(subprojectStepCopy);
            });
          }
        }
        return acc;
      }, []);
    },
  },

  actions: {
    startChangingProcess() {
      const { planning } = usePlanningStore();
      undoState.config = angular.copy(planning.process.config);
      undoState.steps = planning.process.steps.map(step => angular.copy(step));
    },
    changingProcess() {
      const planningStore = usePlanningStore();
      const { planning } = planningStore;
      const newState = {};
      const oldState = {};

      if (! angular.equals(planning.process.config, undoState.config)) {
        newState.config = angular.copy(planning.process.config);
        oldState.config = angular.copy(undoState.config);
      }
      if (! angular.equals(planning.process.steps, undoState.steps)) {
        newState.steps = planning.process.steps.map(step => angular.copy(step));
        oldState.steps = undoState.steps.map(step => angular.copy(step));
      }
      if (! newState.config && ! newState.steps) return;

      useUndoRedoStore().add({
        action: () => {
          set(planning, { config: angular.copy(newState.config), steps: newState.steps?.map(step => angular.copy(step)) });
        },
        rollback: () => {
          set(planning, { config: angular.copy(oldState.config), steps: oldState.steps?.map(step => angular.copy(step)) });
        },
      });
      planningStore.save();
    },
    addStep(newstep) {
      const { planning } = usePlanningStore();
      if (! planning.process.steps) return null;
      this.startChangingProcess();
      const step = insertNewStep(planning, newstep);
      step.id = window.uuid();
      step.label = newstep?.label || i18n.global.t('KANBAN.NEW_COLUMN') || '';
      this.changingProcess();
      return step;
    },
    deleteStep(stepId) {
      const { planning } = usePlanningStore();
      this.startChangingProcess();
      const index = planning.process.steps.findIndex(step => step.id == stepId);
      const step = planning.process.steps[index];
      if (step) {
        usePlanningElementsStore().startChangingElement();
        planning.elements.forEach((el) => {
          if (el.getProcessStep() === stepId) { // send el to backlog
            el.setProcessStep(null);
          }
        });
        usePlanningElementsStore().changingElement();
        planning.process.steps.splice(index, 1);
      }
      this.changingProcess();
    },
  },
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));
export default store;
